/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { ProjectFiles } from '@stackblitz/sdk';
import type { HighlightLang } from '../../components/HighlightCode/types';
import type { ProjectFilesOutput, GetCodeGenericOptions, GetProjectFilesOptions } from './types';

import { CKBOX_URL } from '@ckeditor/ckeditor5-metadata';
import { getCKEditorZipUrl } from '../getCKEditorZipUrl/getCKEditorZipUrl';
import { requiresExportCss } from '../requiresExportCss/requiresExportCss';
import { BlobReader, TextWriter, ZipReader, type Entry } from '@zip.js/zip.js';

import { formatCode } from '../formatCode/formatCode';
import { getCodeAngular } from '../getCodeAngular/getCodeAngular';
import { getCodeAngularHtml } from '../getCodeAngularHtml/getCodeAngularHtml';
import { getCodeCss } from '../getCodeCss/getCodeCss';
import { getCodeCssAngular } from '../getCodeCssAngular/getCodeCssAngular';
import { getCodeHtml } from '../getCodeHtml/getCodeHtml';
import { getCodeJs } from '../getCodeJs/getCodeJs';
import { getCodeReactJs } from '../getCodeReactJs/getCodeReactJs';
import { getCodeVueJs } from '../getCodeVueJs/getCodeVueJs';
import { getExportPackageJson } from '../getExportPackageJson/getExportPackageJson';

/* eslint-disable import/extensions */

import angularAngularJson from '../../assets/integrations/angular/angular.json?raw';
import angularAppConfigTs from '../../assets/integrations/angular/app.config.ts?raw';
import angularAppRoutesTs from '../../assets/integrations/angular/app.routes.ts?raw';
import angularIndexHtml from '../../assets/integrations/angular/index.html?raw';
import angularMainTs from '../../assets/integrations/angular/main.ts?raw';
import angularTsconfigAppJson from '../../assets/integrations/angular/tsconfig.app.json?raw';
import angularTsconfigJson from '../../assets/integrations/angular/_tsconfig.json?raw';
import jsNpmViteConfigJs from '../../assets/integrations/jsNpm/vite.config.js?raw';
import jsZipReadmeMd from '../../assets/integrations/jsZip/README.md?raw';
import reactIndexHtml from '../../assets/integrations/react/index.html?raw';
import reactMainJsx from '../../assets/integrations/react/main.jsx?raw';
import reactViteConfigJs from '../../assets/integrations/react/vite.config.js?raw';
import readmeMd from '../../assets/integrations/README.md?raw';
import stackblitzrc from '../../assets/integrations/.stackblitzrc?raw';
import vueIndexHtml from '../../assets/integrations/vue/index.html?raw';
import vueMainJsx from '../../assets/integrations/vue/main.js?raw';
import vueViteConfigJs from '../../assets/integrations/vue/vite.config.js?raw';

type SourceFile = { filename: string; fileContent: string };

const formattingLangMap: Record<string, HighlightLang> = {
	'.stackblitzrc': 'json'
};

const ckboxAtBodyEndReplacePattern = `<script src="${ CKBOX_URL }" crossorigin></script></body>`;

export async function getProjectFiles( {
	target,
	selectedTechnology,
	hasPremiumPlugins,
	editorOptions,
	selectedInstallMethod,
	customerPortalAccount,
	evaluationLicense,
	isLoggedIn,
	builderUrl
}: GetProjectFilesOptions ): Promise<ProjectFilesOutput> {
	const files = new Files();
	const filesToOpen: Array<string> = [];

	const shouldExportCss = requiresExportCss( {
		editorPlugins: editorOptions.editorPlugins,
		selectedInstallMethod,
		selectedTechnology
	} );

	const getCodeGenericOptions: GetCodeGenericOptions = {
		customerPortalAccount,
		editorOptions,
		evaluationLicense,
		selectedInstallMethod,
		selectedTechnology,
		isLoggedIn,
		hasPremiumPlugins,
		builderUrl
	};

	if ( target === 'stackblitz' ) {
		files.addFile( '.stackblitzrc', stackblitzrc );
	}

	if ( selectedTechnology === 'js' ) {
		filesToOpen.push( 'main.js', 'index.html' );

		files.addFile( 'main.js', getCodeJs( getCodeGenericOptions ) );
		files.addFile( 'style.css', getCodeCss( getCodeGenericOptions ) );

		if ( selectedInstallMethod !== 'zip' ) {
			files.addFile( 'index.html', getCodeHtml( { ...getCodeGenericOptions, includeFavicons: true } ) );
		}

		if ( selectedInstallMethod === 'npm' ) {
			files.addFile( 'vite.config.js', jsNpmViteConfigJs );

			if ( shouldExportCss ) {
				files.addFile( 'public/export-style.css', getCodeCss( getCodeGenericOptions ) );
			}
		}
	}

	if ( selectedTechnology === 'react' ) {
		filesToOpen.push( 'src/App.jsx', 'index.html' );

		files.addFile( 'src/App.css', getCodeCss( getCodeGenericOptions ) );
		files.addFile( 'src/App.jsx', getCodeReactJs( getCodeGenericOptions ) );
		files.addFile( 'src/main.jsx', reactMainJsx );

		if ( editorOptions.editorPlugins.includes( 'CKBox' ) && selectedInstallMethod === 'npm' ) {
			files.addFile( 'index.html', reactIndexHtml.replace( '</body>', ckboxAtBodyEndReplacePattern ) );
		} else {
			files.addFile( 'index.html', reactIndexHtml );
		}

		files.addFile( 'vite.config.js', reactViteConfigJs );

		if ( shouldExportCss ) {
			files.addFile( 'public/export-style.css', getCodeCss( getCodeGenericOptions ) );
		}
	}

	if ( selectedTechnology === 'vue' ) {
		filesToOpen.push( 'src/App.vue', 'index.html' );

		files.addFile( 'src/App.vue', getCodeVueJs( getCodeGenericOptions ) );
		files.addFile( 'src/main.js', vueMainJsx );
		files.addFile( 'src/style.css', getCodeCss( getCodeGenericOptions ) );

		if ( editorOptions.editorPlugins.includes( 'CKBox' ) && selectedInstallMethod === 'npm' ) {
			files.addFile( 'index.html', vueIndexHtml.replace( '</body>', ckboxAtBodyEndReplacePattern ) );
		} else {
			files.addFile( 'index.html', vueIndexHtml );
		}

		files.addFile( 'vite.config.js', vueViteConfigJs );

		if ( shouldExportCss ) {
			files.addFile( 'public/export-style.css', getCodeCss( getCodeGenericOptions ) );
		}
	}

	if ( selectedTechnology === 'angular' ) {
		filesToOpen.push( 'src/app/app.component.ts', 'src/index.html' );

		files.addFile( 'src/app/app.component.html', getCodeAngularHtml( getCodeGenericOptions ) );
		files.addFile( 'src/app/app.component.ts', getCodeAngular( getCodeGenericOptions ) );
		files.addFile( 'src/app/app.component.css', getCodeCssAngular( getCodeGenericOptions ) );
		files.addFile( 'src/app/app.config.ts', angularAppConfigTs );
		files.addFile( 'src/app/app.routes.ts', angularAppRoutesTs );
		files.addFile( 'src/main.ts', angularMainTs );
		files.addFile( 'src/style.css', '' );

		if ( editorOptions.editorPlugins.includes( 'CKBox' ) && selectedInstallMethod === 'npm' ) {
			files.addFile( 'src/index.html', angularIndexHtml.replace( '</body>', ckboxAtBodyEndReplacePattern ) );
		} else {
			files.addFile( 'src/index.html', angularIndexHtml );
		}

		files.addFile( 'angular.json', angularAngularJson );
		files.addFile( 'tsconfig.app.json', angularTsconfigAppJson );
		files.addFile( 'tsconfig.json', angularTsconfigJson );

		if ( shouldExportCss ) {
			files.addFile( 'public/export-style.css', getCodeCss( getCodeGenericOptions ) );
		}
	}

	if ( selectedInstallMethod === 'zip' ) {
		files.addFile( 'index.html', getCodeHtml( { ...getCodeGenericOptions, includeFavicons: true, includeServerWarning: true } ) );
		files.addFile( 'README.md', jsZipReadmeMd );

		const ckeditorSourceFiles = await getCKEditorSourceFiles( hasPremiumPlugins );
		ckeditorSourceFiles.forEach( ( { filename, fileContent } ) => files.addFile( filename, fileContent ) );
	} else {
		files.addFile( 'package.json', getExportPackageJson( selectedTechnology, selectedInstallMethod, hasPremiumPlugins ) );
		files.addFile( 'README.md', readmeMd );
	}

	return {
		filesToOpen,
		projectFiles: await files.getFormattedFiles()
	};
}

async function getCKEditorSourceFiles( hasPremiumPlugins: boolean ): Promise<Array<SourceFile>> {
	const response = await fetch( getCKEditorZipUrl( 'ckeditor5-premium-features' ) );
	const blob = await response.blob();
	const blobReader = new BlobReader( blob );
	const zipReader = new ZipReader( blobReader );
	const entries = await zipReader.getEntries();
	await zipReader.close();

	const promises = entries
		.reduce<Array<Entry>>( ( entriesToProcess, entry ) => {
			if ( entry.filename.endsWith( '/' ) ) {
				return entriesToProcess;
			}

			if ( entry.filename.startsWith( 'ckeditor5/' ) ) {
				entriesToProcess.push( entry );
			}

			if ( entry.filename === 'LICENSE-ckeditor5.md' ) {
				entriesToProcess.push( { ...entry, filename: 'ckeditor5/LICENSE-ckeditor5.md' } );
			}

			if ( !hasPremiumPlugins ) {
				return entriesToProcess;
			}

			if ( entry.filename.startsWith( 'ckeditor5-premium-features/' ) ) {
				entriesToProcess.push( entry );
			}

			if ( entry.filename === 'LICENSE-ckeditor5-premium-features.md' ) {
				entriesToProcess.push( { ...entry, filename: 'ckeditor5-premium-features/LICENSE-ckeditor5-premium-features.md' } );
			}

			return entriesToProcess;
		}, [] )
		.map( async entryToProcess => ( {
			filename: entryToProcess.filename,
			fileContent: await entryToProcess.getData!( new TextWriter() )
		} ) );

	return Promise.all( promises );
}

class Files {
	private files: Array<SourceFile>;

	constructor() {
		this.files = [];
	}

	public addFile( filename: string, fileContent: string ) {
		this.files.push( { filename, fileContent } );
	}

	private getFormattingLang( filename: string ): HighlightLang {
		if ( filename in formattingLangMap ) {
			return formattingLangMap[ filename ];
		}

		const [ matchedLang ] = filename.match( /(?<=.\.)[^.]+$/ )!;

		if ( matchedLang === 'ts' ) {
			return 'angular-ts';
		}

		return matchedLang as HighlightLang;
	}

	public async getFormattedFiles(): Promise<ProjectFiles> {
		const formattedFiles = await Promise.all(
			this.files.map( async ( { filename, fileContent } ) => {
				const shouldSkipFormatting = filename.startsWith( 'ckeditor5/' ) || filename.startsWith( 'ckeditor5-premium-features/' );

				if ( shouldSkipFormatting ) {
					return { filename, fileContent };
				}

				const formattingLang = this.getFormattingLang( filename );

				return {
					filename,
					fileContent: await formatCode( fileContent, formattingLang )
				};
			} )
		);

		return formattedFiles.reduce<ProjectFiles>( ( output, { filename, fileContent } ) => {
			output[ filename ] = fileContent;

			return output;
		}, {} );
	}
}
